import {
  getDeviceCalendarPermissionState,
  listDeviceCalendars,
  listSelectedDeviceCalendarEvents,
} from './deviceCalendars';
import {
  availableSelectedCalendarIds,
  deduplicateExternalCalendarEventsForDisplay,
  ExternalDeviceCalendarEvent,
  isDirectGoogleCalendarEvent,
} from './deviceCalendarsTypes';
import { storage } from '../services/storage';
import { isDeviceCalendarConnectionEnabled } from './calendarConnection';
import { api } from '../services/api';

function localDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseGoogleCalendarEventDate(value: string, allDay: boolean): Date {
  if (!allDay) return new Date(value);
  // API'nin tarih-only sözleşmesini kullan; eski sunucu revizyonlarının aynı
  // değeri ISO zaman damgası olarak döndürmesi halinde de takvim gününü koru.
  const dateOnly = /^(\d{4}-\d{2}-\d{2})/.exec(value)?.[1];
  return new Date(dateOnly ? `${dateOnly}T00:00:00` : value);
}

export async function loadSelectedDeviceCalendarEventsForRange(
  userId: number,
  startDate: Date,
  endDate: Date
): Promise<ExternalDeviceCalendarEvent[]> {
  if (!(await isDeviceCalendarConnectionEnabled(userId))) return [];
  const permission = await getDeviceCalendarPermissionState();
  if (permission !== 'granted') return [];

  const [savedIds, calendars] = await Promise.all([
    storage.getSelectedCalendarIds(userId),
    listDeviceCalendars(),
  ]);
  const selectedIds = availableSelectedCalendarIds(savedIds, calendars);
  if (selectedIds.length !== savedIds.length) {
    await storage.setSelectedCalendarIds(userId, selectedIds);
  }
  return listSelectedDeviceCalendarEvents(selectedIds, calendars, startDate, endDate);
}

export type GoogleCalendarEventLoad = {
  events: ExternalDeviceCalendarEvent[];
  refreshFailed: boolean;
};

/**
 * Kısmi yenilemede yalnız başarılı kaynağı değiştirir. `null`, okumanın
 * başarısız olduğunu ve o kaynağın son görünür belleğinin korunacağını;
 * boş dizi ise okumanın başarılı olduğunu ve kaynağın artık boş olduğunu
 * ifade eder.
 */
export function mergeExternalCalendarRefresh(
  previous: ExternalDeviceCalendarEvent[],
  deviceEvents: ExternalDeviceCalendarEvent[] | null,
  googleEvents: ExternalDeviceCalendarEvent[] | null
): ExternalDeviceCalendarEvent[] {
  const previousDevice = previous.filter((event) => !isDirectGoogleCalendarEvent(event));
  const previousGoogle = previous.filter(isDirectGoogleCalendarEvent);
  return deduplicateExternalCalendarEventsForDisplay([
    ...(deviceEvents ?? previousDevice),
    ...(googleEvents ?? previousGoogle),
  ]);
}

export async function loadSelectedGoogleCalendarEventsForRange(
  token: string,
  startDate: Date,
  endDate: Date
): Promise<GoogleCalendarEventLoad> {
  let refreshFailed = false;
  try {
    await api.syncGoogleCalendar(token);
  } catch {
    // Google geçici olarak erişilemezken sunucudaki son başarılı şifreli önbelleği
    // göstermeye devam et; çağıran yüzey verinin yenilenemediğini ayrıca belirtir.
    refreshFailed = true;
  }
  const response = await api.getGoogleCalendarEvents(token, {
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    startDate: localDateKey(startDate),
    endDate: localDateKey(endDate),
  });
  const events = response.items.flatMap((event) => {
    const start = parseGoogleCalendarEventDate(event.start, event.allDay);
    const end = parseGoogleCalendarEventDate(event.end, event.allDay);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) return [];
    return [{
      id: event.id,
      calendarId: `google:${event.calendarId}`,
      calendarTitle: event.calendarTitle,
      calendarColor: event.calendarColor ?? undefined,
      title: event.title,
      startDate: start,
      endDate: end,
      location: event.location ?? undefined,
      externalIdentity: event.externalIdentity ?? undefined,
      ...(event.originalStart
        ? { originalStartDate: parseGoogleCalendarEventDate(event.originalStart, event.allDay) }
        : {}),
      allDay: event.allDay,
      recurring: event.recurring,
      availability: event.availability,
      responseStatus: event.responseStatus ?? undefined,
      timeZone: event.timeZone ?? undefined,
      busy: event.busy,
    }];
  });
  return { events, refreshFailed };
}

export function findExternalCalendarConflicts(
  events: ExternalDeviceCalendarEvent[],
  startDate: Date,
  endDate: Date
): ExternalDeviceCalendarEvent[] {
  if (!(endDate > startDate)) return [];
  return deduplicateExternalCalendarEventsForDisplay(events).filter((event) =>
    event.responseStatus !== 'declined'
    && event.availability !== 'free'
    && event.availability !== 'tentative'
    && event.busy !== false
    && event.startDate < endDate
    && event.endDate > startDate
  );
}

export function findExternalCalendarTentativeEvents(
  events: ExternalDeviceCalendarEvent[],
  startDate: Date,
  endDate: Date
): ExternalDeviceCalendarEvent[] {
  if (!(endDate > startDate)) return [];
  return deduplicateExternalCalendarEventsForDisplay(events).filter((event) =>
    event.responseStatus !== 'declined'
    && (event.availability === 'tentative' || event.responseStatus === 'tentative')
    && event.startDate < endDate
    && event.endDate > startDate
  );
}

export async function getExternalCalendarConflicts(
  token: string,
  userId: number,
  startDate: Date,
  durationMinutes: number
): Promise<{
  events: ExternalDeviceCalendarEvent[];
  tentativeEvents: ExternalDeviceCalendarEvent[];
  incomplete: boolean;
}> {
  const safeDuration = Math.max(1, Math.floor(durationMinutes));
  const endDate = new Date(startDate.getTime() + safeDuration * 60_000);
  const [deviceResult, googleResult] = await Promise.allSettled([
    loadSelectedDeviceCalendarEventsForRange(userId, startDate, endDate),
    loadSelectedGoogleCalendarEventsForRange(token, startDate, endDate),
  ]);
  const events = deduplicateExternalCalendarEventsForDisplay([
    ...(deviceResult.status === 'fulfilled' ? deviceResult.value : []),
    ...(googleResult.status === 'fulfilled' ? googleResult.value.events : []),
  ]);
  return {
    events: findExternalCalendarConflicts(events, startDate, endDate),
    tentativeEvents: findExternalCalendarTentativeEvents(events, startDate, endDate),
    incomplete: deviceResult.status === 'rejected'
      || googleResult.status === 'rejected'
      || (googleResult.status === 'fulfilled' && googleResult.value.refreshFailed),
  };
}
